/**
 * pdp-variant-bundle-selector.js
 *
 * The product page's "Variant & bundle selector" block (snippets/pdp-variant-bundle-selector.liquid).
 * Served as assets/pdp-variant-bundle-selector.js, a byte copy of this file, outside the Vite bundle.
 *
 *   - Step 1 is a <variant-picker>: the theme selects the variant, so this file only keeps the total in step.
 *   - Step 2 keeps the chosen add-on bundle. Each bundle has an Add button ([data-pvbs-toggle]): a click on the chosen
 *     bundle selects the hidden "Scooter only" radio, which removes it. Once the theme's product form has added the
 *     scooter (its variant:add event), the bundle is added, one per scooter, and the cart drawer is refreshed the way the theme refreshes it.
 *     Adding after the scooter means a failed scooter add never leaves a cart holding only the bundle.
 *   - The dynamic checkout button ("Buy it now") skips the cart, so it is hidden while a bundle is chosen.
 *   - The total (variant plus bundle) is carried to the add to cart button's price ("Add to cart - $908") and to the
 *     Affirm monthly price of the "Price" block (<affirm-primary-price>, which asks Affirm for the new amount), and to
 *     the "Buy box header" block's "or $999 today" ([data-pvbs-today]), whose compare-at price hides with a bundle (its sale pill stays).
 *   - The What's covered dialog's [data-pvbs-term] follows the selected card's coverage term (its data-term).
 *   - [data-pvbs-dialog-open] buttons open their <dialog> ("What's covered", the guarantees of the
 *     "Stock, draw & guarantees" block, snippets/pdp-buy-box-trust.liquid), and that block's stock line follows the
 *     selected variant.
 *   - At the end of every update it fires pvbs:total ({ formId, totalCents, totalText }) on the document, which the
 *     "Payment method" block (pdp-payment-method.js) follows. That block's payment:change carries a labelSuffix
 *     ("from $92/mo", "$45 to start") that takes the total's place on the add to cart button; null puts the total back.
 *   - Apollo Ship+ ([data-pvbs-ship-plus], beside the "Payment method" block only): a tick box priced the way Navidium's
 *     cart widget prices it (shipPlusCents, from its nvdWidgetConfig on the page), added to the total while ticked. The
 *     tick is shared with the Build summary cart through sessionStorage (cs-ship-plus), whose own Ship+ row then starts
 *     ticked; Ship+ reaches the order through Navidium at Checkout, as in the cart. No price, no row.
 *   - The stores use the cart drawer; with the "page" cart type the theme leaves the page before variant:add, and the
 *     bundle is not added.
 */
(function (global) {
  'use strict';

  // Both blocks load this file; the second copy does nothing.
  if (global.PdpVariantBundleSelector) return;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function addDelimiters(whole, thousands) {
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  }

  /**
   * Format an amount in cents with the shop's money format ("${{amount}}", "{{amount_with_comma_separator}} $"),
   * dropping trailing zeros like Liquid's money_without_trailing_zeros.
   */
  function formatMoney(cents, format) {
    var value = Math.round(Number(cents) || 0);
    var pattern = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '${{amount}}';
    var match = fmt.match(pattern);
    var kind = match ? match[1] : 'amount';
    var decimals = 2;
    var thousands = ',';
    var decimal = '.';

    if (kind === 'amount_no_decimals') {
      decimals = 0;
    } else if (kind === 'amount_with_comma_separator') {
      thousands = '.';
      decimal = ',';
    } else if (kind === 'amount_no_decimals_with_comma_separator') {
      decimals = 0;
      thousands = '.';
    } else if (kind === 'amount_with_apostrophe_separator') {
      thousands = "'";
    } else if (kind === 'amount_with_period_and_space_separator') {
      thousands = ' ';
    } else if (kind === 'amount_with_space_separator') {
      thousands = ' ';
      decimal = ',';
    }

    var negative = value < 0;
    var fixed = (Math.abs(value) / 100).toFixed(decimals);
    var parts = fixed.split('.');
    var text = addDelimiters(parts[0], thousands);
    if (decimals > 0 && parts[1] && parts[1] !== '00') {
      text += decimal + parts[1];
    }
    var out = match ? fmt.replace(pattern, text) : text;
    return (negative ? '-' : '') + out.replace(/<[^>]*>/g, '');
  }

  var SHIP_PLUS_KEY = 'cs-ship-plus';

  /**
   * Navidium's price for protecting `totalCents`, in cents, as its cart widget works it out (nvdCalculateProtection):
   * the percentage of the total cut to the cent, held between the minimum and maximum, then rounded to the next of its
   * variant prices ($0.75 steps). Null when the settings are not the percentage kind this mirrors (static rules, an
   * international fee, another currency), so the row stays hidden rather than show a price the cart will not charge.
   */
  function shipPlusCents(totalCents, config, rate) {
    if (!config || !(totalCents > 0)) return null;
    if (String(config.nvd_protection_type) !== '1') return null;
    if (String(config.nvd_international_fee_active) === '1') return null;
    if (rate !== undefined && rate !== null && parseFloat(rate) !== 1) return null;
    var percentage = parseFloat(config.nvd_protection_type_value);
    if (!(percentage > 0)) return null;
    var price = Math.floor((((totalCents / 100) * percentage) / 100) * 100) / 100;
    var min = parseFloat(config.min_protection_value);
    var max = parseFloat(config.max_protection_value);
    if (!isFinite(min) || !isFinite(max) || !isFinite(price)) return null;
    if (price < min) return Math.round(min * 100);
    if (price > max) return Math.round(max * 100);
    var steps = Object.keys(config.nvd_variants || {})
      .map(Number)
      .filter(function (value) {
        return value > 0;
      })
      .sort(function (a, b) {
        return a - b;
      });
    if (!steps.length) return null;
    var down = config.rounding_value !== 'rounding_up';
    var result = 0;
    for (var i = 0; i < steps.length; i += 1) {
      var next = steps[i + 1];
      if (price >= steps[i] && price <= next) {
        result = down ? steps[i] : next;
        break;
      } else if (price <= steps[i]) {
        result = steps[i];
        break;
      }
    }
    if (!result) return Math.round(max * 100);
    return Math.round(result * 100);
  }

  /** Navidium's settings, which its app embed declares on every page (a top-level const, so not on window). */
  function navidiumConfig() {
    if (global.nvdWidgetConfig) return global.nvdWidgetConfig;
    try {
      // eslint-disable-next-line no-undef
      return typeof nvdWidgetConfig !== 'undefined' ? nvdWidgetConfig : null;
    } catch (error) {
      return null;
    }
  }

  function currencyRate() {
    return global.Shopify && global.Shopify.currency ? global.Shopify.currency.rate : undefined;
  }

  function storedShipPlus() {
    try {
      return global.sessionStorage.getItem(SHIP_PLUS_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function storeShipPlus(on) {
    try {
      global.sessionStorage.setItem(SHIP_PLUS_KEY, on ? '1' : '0');
    } catch (error) {
      // Private mode: the cart starts unticked.
    }
    // The Build summary cart's row follows at once (sessionStorage fires no event in its own tab).
    global.document.dispatchEvent(new global.CustomEvent('ship-plus:change', { detail: { on: Boolean(on), source: 'pdp' } }));
  }

  function checked(root, selector) {
    return toArray(root.querySelectorAll(selector)).filter(function (input) {
      return input.checked;
    })[0] || null;
  }

  function priceOf(input) {
    return input ? parseInt(input.getAttribute('data-price'), 10) || 0 : 0;
  }

  /** The total the summary shows: the selected variant's price plus the chosen bundle's. */
  function totalFor(variantPrice, bundlePrice) {
    return (Number(variantPrice) || 0) + (Number(bundlePrice) || 0);
  }

  /**
   * The text before " - <price>" when a label is the theme's "Add to cart - $799" for that price, else null: a
   * label without the price ("Pre-order", "Sold out", a custom text such as "Reserve now - ships in June") is never
   * rewritten.
   */
  function labelPrefix(label, priceText) {
    var text = String(label || '').trim();
    var suffix = ' - ' + priceText;
    if (!priceText || text.length <= suffix.length || text.slice(-suffix.length) !== suffix) return null;
    return text.slice(0, -suffix.length).trim() || null;
  }

  /**
   * The element of a button that holds its label, "<div>Add to cart - <span class="money">$799</span></div>": the
   * first child that is not the loading spinner, or the button itself.
   */
  function labelElement(button) {
    return toArray(button.children).filter(function (child) {
      return !child.classList.contains('button__loader');
    })[0] || button;
  }

  /** Put the total in the label: in its money element when it has one (a currency app converts that), else as text. */
  function writeLabel(element, prefix, totalText) {
    var money = element.querySelector('[class*="money"]');
    if (money) {
      if (money.textContent !== totalText) money.textContent = totalText;
      return;
    }
    var next = prefix + ' - ' + totalText;
    if (element.textContent.trim() !== next) element.textContent = next;
  }

  function openDialog(id) {
    var dialog = global.document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (!dialog.open) dialog.showModal();
    return true;
  }

  /** A click on the backdrop lands on the dialog itself, outside its box; one on its border or scrollbar is inside. */
  function outside(dialog, event) {
    var box = dialog.getBoundingClientRect();
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
  }

  /** Dialog buttons, the close buttons inside, and a click on the backdrop. */
  function onDocumentClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var opener = target.closest('[data-pvbs-dialog-open]');
    if (opener) {
      if (openDialog(opener.getAttribute('data-pvbs-dialog-open'))) event.preventDefault();
      return;
    }
    var closer = target.closest('[data-pvbs-dialog-close]');
    var dialog = closer ? closer.closest('dialog') : null;
    if (!dialog && target.tagName === 'DIALOG' && target.classList.contains('pvbs-dialog') && outside(target, event)) {
      dialog = target;
    }
    if (dialog && dialog.open) dialog.close();
  }

  /** The stock line of the "Stock, draw & guarantees" block follows the selected variant. */
  function onStockVariantChange(event) {
    var target = event.target;
    var variant = event.detail && event.detail.variant;
    if (!variant || !target || !target.getAttribute) return;
    var formId = target.getAttribute('id');
    toArray(global.document.querySelectorAll('[data-bbt]')).forEach(function (block) {
      if (block.getAttribute('data-form-id') !== formId) return;
      var line = block.querySelector('[data-bbt-stock]');
      if (line) line.hidden = variant.available === false;
    });
  }

  function selectorFor(form) {
    var formId = form && form.getAttribute && form.getAttribute('id');
    if (!formId) return null;
    return toArray(global.document.querySelectorAll('pdp-variant-bundle-selector')).filter(function (el) {
      return el.getAttribute('data-form-id') === formId;
    })[0] || null;
  }

  function rootUrl() {
    return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
  }

  /**
   * How many scooters the form just added: one bundle goes with each. Read from the form, not the cart/add response,
   * whose quantity is the line's whole quantity in the cart.
   */
  function addedQuantity(form) {
    // form.id is the form's "id" input (the variant), not its id attribute.
    var field = form.querySelector('[name="quantity"]') || global.document.querySelector('[name="quantity"][form="' + form.getAttribute('id') + '"]');
    var quantity = field ? parseInt(field.value, 10) : 1;
    return quantity > 0 ? quantity : 1;
  }

  /**
   * Add the bundle and refresh the cart as the theme's product form does after its own add: the cart sections are
   * bundled into the add, then cart:change carries the new cart to the drawer and the header count.
   */
  function addBundle(form, variantId, quantity) {
    var doc = global.document;
    var sections = [];
    doc.documentElement.dispatchEvent(
      new global.CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    var body = { items: [{ id: Number(variantId), quantity: quantity }] };
    if (sections.length) body.sections = sections.join(',');

    return global
      .fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      })
      .then(function (response) {
        return response.json().then(function (json) {
          if (!response.ok) throw new Error((json && json.description) || 'cart/add failed');
          return global.fetch(rootUrl() + 'cart.js').then(function (cartResponse) {
            return cartResponse.json().then(function (cart) {
              cart.sections = json.sections;
              doc.documentElement.dispatchEvent(
                new global.CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } })
              );
            });
          });
        });
      })
      .catch(function (error) {
        // The scooter is in the cart already. Say the bundle is not (it sold out since the page loaded, say) with the
        // theme's own error banner on the form, and refresh the cart.
        form.dispatchEvent(
          new global.CustomEvent('cart:error', { bubbles: true, detail: { error: (error && error.message) || 'cart/add failed' } })
        );
        doc.dispatchEvent(new global.CustomEvent('cart:refresh'));
      });
  }

  /** The theme's product form added a line: add the chosen bundle with it. */
  function onVariantAdded(event) {
    var form = event.target;
    if (!form || !form.getAttribute) return null;
    var el = selectorFor(form);
    if (!el) return null;
    var bundleId = el.selectedBundleId();
    if (!bundleId) return null;
    return addBundle(form, bundleId, addedQuantity(form));
  }

  function define() {
    var HTMLElementBase = global.HTMLElement;
    if (!global.customElements || !HTMLElementBase || global.customElements.get('pdp-variant-bundle-selector')) return;

    global.customElements.define('pdp-variant-bundle-selector', class extends HTMLElementBase {
      connectedCallback() {
        this.update = this.update.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onPaymentChange = this.onPaymentChange.bind(this);
        this.paymentSuffix = null;
        this.variantPrice = parseInt(this.getAttribute('data-variant-price'), 10);
        this.onShipPlusChange = this.onShipPlusChange.bind(this);
        this.shipPlusInput = this.querySelector('[data-pvbs-ship-plus-input]');
        if (this.shipPlusInput) {
          this.shipPlusInput.checked = storedShipPlus();
          this.shipPlusInput.addEventListener('change', this.onShipPlusChange);
          // Navidium's settings may be declared after this script runs.
          if (!navidiumConfig() && global.document.readyState !== 'complete') {
            this.loadListener = this.update;
            global.addEventListener('load', this.loadListener, { once: true });
          }
          // A tick changed in the cart drawer comes back here.
          this.onCartShipPlus = function (event) {
            var detail = (event && event.detail) || {};
            if (detail.source === 'pdp' || !this.shipPlusInput) return;
            if (this.shipPlusInput.checked === Boolean(detail.on)) return;
            this.shipPlusInput.checked = Boolean(detail.on);
            this.update();
          }.bind(this);
          global.document.addEventListener('ship-plus:change', this.onCartShipPlus);
        }
        this.addEventListener('change', this.update);
        this.addEventListener('click', this.onClick);
        global.document.addEventListener('variant:change', this.onVariantChange);
        global.document.addEventListener('payment:change', this.onPaymentChange);
        this.update();
      }

      disconnectedCallback() {
        if (this.shipPlusInput) this.shipPlusInput.removeEventListener('change', this.onShipPlusChange);
        if (this.loadListener) global.removeEventListener('load', this.loadListener);
        if (this.onCartShipPlus) global.document.removeEventListener('ship-plus:change', this.onCartShipPlus);
        this.removeEventListener('change', this.update);
        this.removeEventListener('click', this.onClick);
        global.document.removeEventListener('variant:change', this.onVariantChange);
        global.document.removeEventListener('payment:change', this.onPaymentChange);
      }

      /** The tick is the shopper's choice for the cart too. */
      onShipPlusChange() {
        storeShipPlus(this.shipPlusInput.checked);
      }

      /**
       * The Ship+ row for the build's total: shown with Navidium's price, or hidden (and left out of the total) when
       * there is no price to show. Returns the cents to add.
       */
      syncShipPlus(buildCents, format) {
        var wrap = this.querySelector('[data-pvbs-ship-plus]');
        var summary = this.querySelector('[data-pvbs-ship-plus-row]');
        if (!wrap || !this.shipPlusInput) {
          if (summary) summary.hidden = true;
          return 0;
        }
        var price = shipPlusCents(buildCents, navidiumConfig(), currencyRate());
        wrap.hidden = price === null;
        var priceText = price === null ? '' : formatMoney(price, format);
        toArray(wrap.querySelectorAll('[data-pvbs-ship-plus-price]')).forEach(function (element) {
          if (element.textContent !== priceText) element.textContent = priceText;
        });
        this.shipPlusInput.setAttribute('data-price-delta', price === null ? '' : String(price / 100));
        var on = price !== null && this.shipPlusInput.checked;
        if (summary) {
          summary.hidden = !on;
          var amount = summary.querySelector('[data-pvbs-ship-plus-total]');
          if (amount && amount.textContent !== priceText) amount.textContent = priceText;
        }
        return on ? price : 0;
      }

      /** The "Payment method" block's label suffix ("from $92/mo") replaces the total on the add to cart button. */
      onPaymentChange(event) {
        var detail = (event && event.detail) || {};
        if (detail.formId !== this.getAttribute('data-form-id')) return;
        var suffix = detail.labelSuffix ? String(detail.labelSuffix) : null;
        if (suffix === this.paymentSuffix) return;
        this.paymentSuffix = suffix;
        if (this.lastLabel) this.syncButtonLabel(this.lastLabel.variantPriceText, this.lastLabel.totalText);
      }

      /**
       * Add buttons: a click (or Space) on the bundle that was already chosen removes it. A radio that is clicked while
       * checked stays checked and fires no change, so the bundle chosen at the last update tells the two apart.
       */
      onClick(event) {
        var input = event && event.target;
        if (!input || !input.matches || !input.matches('[data-pvbs-toggle] input[data-pvbs-bundle]')) return;
        if (!input.value || input !== this.chosenBundle) return;
        var none = toArray(this.querySelectorAll('input[data-pvbs-bundle]')).filter(function (item) {
          return item.name === input.name && !item.value;
        })[0];
        if (!none) return;
        none.checked = true;
        this.update();
      }

      selectedBundleId() {
        var input = checked(this, 'input[data-pvbs-bundle]');
        return input && input.value ? input.value : null;
      }

      onVariantChange(event) {
        var target = event.target;
        if (!target || !target.getAttribute || target.getAttribute('id') !== this.getAttribute('data-form-id')) return;
        var variant = event.detail && event.detail.variant;
        if (variant && typeof variant.price === 'number') this.variantPrice = variant.price;
        if (variant && variant.id) {
          var input = toArray(this.querySelectorAll('input[data-pvbs-variant]')).filter(function (item) {
            return item.getAttribute('data-pvbs-variant') === String(variant.id);
          })[0];
          if (input && !input.checked) input.checked = true;
        }
        this.update();
      }

      /** Hide the dynamic checkout button while a bundle is chosen: it would buy the scooter alone. */
      syncPaymentButton(hasBundle) {
        var form = global.document.getElementById(this.getAttribute('data-form-id'));
        if (!form) return;
        toArray(form.querySelectorAll('.shopify-payment-button')).forEach(function (button) {
          button.style.display = hasBundle ? 'none' : '';
        });
      }

      /**
       * "Add to cart - $799" on the form's buttons becomes the total. The label is read once per button, while it still
       * shows the variant's price; a button the theme re-renders for a new variant is read again.
       */
      syncButtonLabel(variantPriceText, totalText) {
        var formId = this.getAttribute('data-form-id');
        this.lastLabel = { variantPriceText: variantPriceText, totalText: totalText };
        var shown = this.paymentSuffix || totalText;
        toArray(global.document.querySelectorAll('buy-buttons[form="' + formId + '"] button[type="submit"]')).forEach(function (button) {
          var element = labelElement(button);
          var prefix = button.getAttribute('data-pvbs-label');
          if (prefix === null) {
            prefix = labelPrefix(element.textContent, variantPriceText) || '';
            button.setAttribute('data-pvbs-label', prefix);
          }
          if (prefix) writeLabel(element, prefix, shown);
        });
      }

      /**
       * The Affirm monthly price follows the total once a bundle is chosen, and goes back to the variant's price when
       * the shopper returns to "Scooter only". The theme re-renders the price block on a variant change, and the new
       * one asks Affirm for the variant's price; asking for the total after it wins (the element keeps the latest).
       */
      /** The product's blocks the theme re-renders: the price, the buy box header, the add to cart button. */
      productScope() {
        return this.closest('product-rerender') || this.closest('.product-info') || global.document;
      }

      syncAffirm(total, hasBundle) {
        if (!hasBundle && !this.affirmBundled) return;
        this.affirmBundled = hasBundle;
        var scope = this.productScope();
        // After every other variant:change listener, the re-rendered price block's own among them.
        global.clearTimeout(this.affirmTimer);
        this.affirmTimer = global.setTimeout(function () {
          toArray(scope.querySelectorAll('affirm-primary-price')).forEach(function (price) {
            // The "Payment method" block asks its own Affirm price for the total.
            if (price.closest('pdp-payment-method')) return;
            if (typeof price.refresh === 'function') price.refresh(total);
          });
        }, 0);
      }

      /**
       * The buy box header's "or $999 today" shows the total (the variant's price alone without a bundle, formatted as
       * Liquid's money_without_trailing_zeros does). Its compare-at price is the scooter's alone, so it hides while a
       * bundle is chosen; the sale pill stays (the promotion still applies). Nothing to do until a bundle has been chosen
       * once.
       */
      syncToday(totalText, hasBundle) {
        if (!hasBundle && !this.todayBundled) return;
        this.todayBundled = hasBundle;
        var scope = this.productScope();
        toArray(scope.querySelectorAll('[data-pvbs-today]')).forEach(function (element) {
          if (element.textContent !== totalText) element.textContent = totalText;
        });
        toArray(scope.querySelectorAll('[data-pvbs-today-compare]')).forEach(function (element) {
          element.hidden = hasBundle;
        });
      }

      update() {
        var format = this.getAttribute('data-money-format');
        var variantInput = checked(this, 'input[data-pvbs-variant]');
        var bundleInput = checked(this, 'input[data-pvbs-bundle]');
        this.chosenBundle = bundleInput && bundleInput.value ? bundleInput : null;
        if (variantInput) this.variantPrice = priceOf(variantInput);
        var variantPrice = this.variantPrice;

        var base = this.querySelector('[data-pvbs-base-price]');
        if (base && !isNaN(variantPrice)) base.textContent = formatMoney(variantPrice, format);

        var title = this.querySelector('[data-pvbs-variant-title]');
        var delta = this.querySelector('[data-pvbs-variant-delta]');
        if (variantInput && title) title.textContent = variantInput.getAttribute('data-title');
        if (variantInput && delta) delta.textContent = variantInput.getAttribute('data-delta-label');

        // "[term]" in the What's covered dialog: the selected card's coverage term ("1 year", "2 years").
        // A card without a term empties it rather than leaving another card's coverage on show.
        if (variantInput) {
          var term = variantInput.getAttribute('data-term') || '';
          toArray(this.querySelectorAll('[data-pvbs-term]')).forEach(function (element) {
            if (element.textContent !== term) element.textContent = term;
          });
        }

        var row = this.querySelector('[data-pvbs-bundle-row]');
        if (row) {
          var bundlePrice = priceOf(bundleInput);
          row.hidden = !(bundleInput && bundleInput.value);
          var bundleTitle = row.querySelector('[data-pvbs-bundle-title]');
          var bundleAmount = row.querySelector('[data-pvbs-bundle-price]');
          if (bundleTitle) bundleTitle.textContent = bundleInput ? bundleInput.getAttribute('data-title') : '';
          if (bundleAmount) bundleAmount.textContent = formatMoney(bundlePrice, format);
        }

        if (isNaN(variantPrice)) return;
        var hasBundle = Boolean(bundleInput && bundleInput.value);
        var build = totalFor(variantPrice, priceOf(bundleInput));
        var shipPlus = this.syncShipPlus(build, format);
        var amount = build + shipPlus;
        // The header's compare-at and the Price block's Affirm figure are the scooter's alone: they follow the total
        // whenever anything is added to it.
        var hasExtra = hasBundle || shipPlus > 0;
        // The dynamic checkout button buys the scooter alone: no bundle, and no Ship+ (it skips Navidium's checkout).
        this.syncPaymentButton(hasExtra);
        var totalText = formatMoney(amount, format);
        this.totalCents = amount;
        var total = this.querySelector('[data-pvbs-total]');
        if (total) total.textContent = totalText;
        this.syncButtonLabel(formatMoney(variantPrice, format), totalText);
        this.syncToday(totalText, hasExtra);
        this.syncAffirm(amount, hasExtra);
        global.document.dispatchEvent(
          new global.CustomEvent('pvbs:total', {
            detail: { formId: this.getAttribute('data-form-id'), totalCents: amount, totalText: totalText }
          })
        );
      }
    });
  }

  global.PdpVariantBundleSelector = {
    formatMoney: formatMoney,
    totalFor: totalFor,
    shipPlusCents: shipPlusCents,
    labelPrefix: labelPrefix,
    onVariantAdded: onVariantAdded,
    define: define
  };

  if (global.document) {
    define();
    if (!global.__pvbsAddBound) {
      global.__pvbsAddBound = true;
      global.document.addEventListener('variant:add', onVariantAdded);
      global.document.addEventListener('click', onDocumentClick);
      global.document.addEventListener('variant:change', onStockVariantChange);
    }
  }
})(typeof window !== 'undefined' ? window : this);
